/**
 * Phase 3d — Biometric login adapter.
 *
 * Stores the existing driver JWT in the device's secure store (iOS Keychain /
 * Android Keystore) and requires Face ID / Touch ID / fingerprint to retrieve
 * it. **No backend change** — the same `/api/driver/login` flow issues the JWT;
 * we simply gate its retrieval on biometric verification.
 *
 * Plugin: `capacitor-native-biometric` (community, MIT). Selected because it
 * supports Capacitor 7 and exposes a single Keychain/Keystore-backed
 * secure-credentials API that doesn't need extra crypto wiring.
 *
 * Storage convention:
 *   server: "transport247.driver"
 *   username: driver email
 *   password: JWT token
 *
 * Public API:
 *   isAvailable() -> Promise<boolean>
 *   enable({ email, jwt }) -> Promise<{ok, reason?}>
 *   tryUnlock() -> Promise<{ok, email?, jwt?, reason?}>
 *   disable() -> Promise<void>
 */
import { isNative } from "./platform";

const SERVER = "transport247.driver";

async function _loadPlugin() {
  try {
    const mod = await import("capacitor-native-biometric");
    return mod?.NativeBiometric || null;
  } catch {
    return null;
  }
}

export async function isAvailable() {
  if (!(await isNative())) return false;
  const NB = await _loadPlugin();
  if (!NB) return false;
  try {
    const result = await NB.isAvailable();
    return !!result?.isAvailable;
  } catch {
    return false;
  }
}

export async function enable({ email, jwt } = {}) {
  if (!email || !jwt) return { ok: false, reason: "missing_params" };
  if (!(await isAvailable())) return { ok: false, reason: "not_available" };
  const NB = await _loadPlugin();
  try {
    await NB.setCredentials({ username: email, password: jwt, server: SERVER });
    return { ok: true };
  } catch (e) {
    return { ok: false, reason: String(e?.message || e) };
  }
}

export async function tryUnlock() {
  if (!(await isAvailable())) return { ok: false, reason: "not_available" };
  const NB = await _loadPlugin();
  try {
    // Verify the user (Face ID / fingerprint prompt)
    await NB.verifyIdentity({
      reason: "Sign in to Transport 247 Driver",
      title: "Driver login",
      subtitle: "Use biometrics to continue",
      description: "Confirm it's you to unlock your driver account.",
    });
    const creds = await NB.getCredentials({ server: SERVER });
    if (!creds?.password) return { ok: false, reason: "no_stored_token" };
    return { ok: true, email: creds.username, jwt: creds.password };
  } catch (e) {
    return { ok: false, reason: String(e?.message || e) };
  }
}

export async function disable() {
  const NB = await _loadPlugin();
  if (!NB) return;
  try { await NB.deleteCredentials({ server: SERVER }); } catch { /* ignore */ }
}
